import React, { useState, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import editProfileUser from "assets/images/others/host-side-images/editProfileUSer.png";
import useOutsideClick from "hooks/click-outside";
import useHook from "../useHooks";
import { RegisterRsvpDTO } from "utils/helpers/models/host-dtos/register-rsvp.dto";
import { warningToaster } from "utils/helpers/common/alert-service";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { ReactComponent as EditProfile } from "assets/images/svgs/edit-profiles.svg";

type AddRepresentativeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAddComplete: () => void;
};

const AddRepresentativeModal: React.FC<AddRepresentativeModalProps> = ({
  isOpen,
  onClose,
  onAddComplete,
}) => {
  const { uploadProfile, registerRsvp } = useHook();
  const [formData, setFormData] = useState<RegisterRsvpDTO>(new RegisterRsvpDTO());
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    getValues,
    setError,
    clearErrors,
    trigger,
  } = useForm<RegisterRsvpDTO>({ defaultValues: formData });

  const handleChange = (event: any) => {
    const {
      target: { value, name },
    } = event;
    
    // Capitalize first letter of firstName and lastName
    let processedValue = value;
    if (name === "firstName" || name === "lastName") {
      processedValue = value
        .split(" ")
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    }
    
    setValue(name, processedValue, { shouldValidate: true });
    setFormData({ ...formData, [name]: processedValue });
  };

  const onSubmit: SubmitHandler<RegisterRsvpDTO> = async () => {
    const profilePicId = getValues("profilePicId");
    if (!profilePicId) {
      setError("profilePicId", {
        type: "required",
        message: "Please upload a profile picture",
      });
      warningToaster(
        "Please upload a profile picture before creating the representative."
      );
      return;
    }

    await registerRsvp(formData);
    onAddComplete();
    onClose();
    reset();
    setProfileImage(null);
    setFormData(new RegisterRsvpDTO());
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Clear error immediately when file is selected
      clearErrors("profilePicId");
      
      const previewUrl = URL.createObjectURL(file);
      setProfileImage(previewUrl);
      setValue("profilePicUrl", file.name, { shouldValidate: false });

      try {
        const form_data = new FormData();
        form_data.append("file", file);
        const uploaded = await uploadProfile(form_data);
        if (uploaded?.id) {
          setValue("profilePicId", uploaded.id, { shouldValidate: false });
          setFormData((prev) => ({ ...prev, profilePicId: uploaded.id }));
          // Clear any errors after successful upload
          clearErrors("profilePicId");
        }
      } catch (error) {
        console.error("Upload failed", error);
        warningToaster("Failed to upload profile picture. Please try again.");
        setError("profilePicId", {
          type: "upload",
          message: "Failed to upload profile picture. Please try again.",
        });
      }
    }
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset();
      setProfileImage(null);
      setFormData(new RegisterRsvpDTO());
    }
  }, [isOpen, reset]);

  const modalRef = useOutsideClick(onClose);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ zIndex: 'calc(var(--z-modal) + 1)' }}
    >
      <div
        ref={modalRef}
        className={`bg-white px-6 py-8 rounded-lg shadow-lg w-[90%] sm:w-[440px] transform transition-transform duration-300 ${
          isOpen ? "scale-100" : "scale-95"
        }`}
      >
        <h2 className="text-xl text-center text-primaryBlueText font-bold mb-4">
          Add Representative
        </h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col items-center w-full">
            <div className="relative">
              <img
                src={
                  profileImage
                    ? typeof profileImage === "string" && profileImage.startsWith("blob:")
                      ? profileImage
                      : concatPathWithBackendUrl(profileImage)
                    : editProfileUser
                }
                alt="Profile"
                className="w-36 h-36 rounded-full object-cover"
              />
              <label
                htmlFor="profilePicUrl"
                className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
              >
                <EditProfile />
              </label>
              <input
                id="profilePicUrl"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
              {/* Hidden input for profilePicId validation */}
              <input
                type="hidden"
                {...register("profilePicId", {
                  required: "Please upload a profile picture",
                })}
              />
            </div>
            {errors.profilePicId && (
              <p className="text-red-500 text-sm mt-1">
                {errors.profilePicId.message}
              </p>
            )}
          </div>

          {/* Text Fields Section */}
          <div className="w-full my-4 grid grid-cols-1 gap-3">
            <div className="flex flex-col gap-1">
              <label className="custom-label">
                First Name
              </label>
              <input
                type="text"
                className="custom-input"
                {...register("firstName", {
                  required: "First Name is required",
                })}
                value={formData.firstName}
                onChange={handleChange}
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label className="custom-label">
                Last Name
              </label>
              <input
                type="text"
                className="custom-input"
                {...register("lastName", { required: "Last Name is required" })}
                value={formData.lastName}
                onChange={handleChange}
              />
              {errors.lastName && (
                <p className="text-red-500 text-sm">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>

          <div className="w-full my-4 grid grid-cols-1 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-label text-base font-semibold">
                Email
              </label>
              <input
                type="email"
                className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: "Enter a valid email",
                  },
                })}
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email.message}</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="grid grid-cols-1 mt-6">
            <button
              type="submit"
              className="h-[62px] mt-2 text-base bg-primaryBlueBg rounded-full text-white w-full"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRepresentativeModal;
